import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, ButtonBase, Divider, Popover, Stack, Tooltip, Typography,
} from '@mui/material';
import CursorDefaultOutlineIcon from 'mdi-react/CursorDefaultOutlineIcon';
import DoorOpenIcon from 'mdi-react/DoorOpenIcon';
import TextureBoxIcon from 'mdi-react/TextureBoxIcon';
import VectorSquareEditIcon from 'mdi-react/VectorSquareEditIcon';
import DeskIcon from 'mdi-react/DeskIcon';
import FormatTextIcon from 'mdi-react/FormatTextIcon';
import ShapeOutlineIcon from 'mdi-react/ShapeOutlineIcon';
import GridIcon from 'mdi-react/GridIcon';
import type { EditMode } from '../models/edit-mode';
import { isCorneredTool, type DrawTarget, type EditorTool } from '../models/editor-tool';
import { editorTheme, floatingSlabSx } from '../utils/editor-theme';
import { BASIC_SHAPES, type BasicShapeTool } from './basic-shapes';

interface Props {
  readonly mode: EditMode;
  readonly tool: EditorTool;
  readonly onModeChange: (mode: EditMode) => void;
  /** Arms one of the basic shapes, to be drawn on the floor. */
  readonly onShapeTool: (tool: BasicShapeTool) => void;
  readonly showGrid: boolean;
  readonly onShowGridChange: (show: boolean) => void;
  /**
   * Which layer the armed drawing tool draws on. A rectangle armed from the Rooms panel draws a
   * room, not a floor shape, so it must not light up the Shapes button.
   */
  readonly drawTarget?: DrawTarget;
  /**
   * Drawn as a plain column in a side bar, rather than as a slab floating over the plan.
   * The side bar supplies the edge and the background, so the toolbox does not add its own.
   */
  readonly docked?: boolean;
}

const MODES: ReadonlyArray<{
  readonly mode: EditMode;
  readonly labelKey: string;
  readonly hintKey: string;
  readonly Icon: typeof CursorDefaultOutlineIcon;
}> = [
  {
    mode: 'select', labelKey: 'floorplan-simple-mode-select', hintKey: 'floorplan-simple-mode-select-hint', Icon: CursorDefaultOutlineIcon,
  },
  {
    mode: 'rooms', labelKey: 'floorplan-simple-mode-rooms', hintKey: 'floorplan-simple-mode-rooms-hint', Icon: DoorOpenIcon,
  },
  {
    mode: 'floor', labelKey: 'floorplan-simple-mode-floor', hintKey: 'floorplan-simple-mode-floor-hint', Icon: TextureBoxIcon,
  },
  {
    mode: 'border', labelKey: 'floorplan-simple-mode-border', hintKey: 'floorplan-simple-mode-border-hint', Icon: VectorSquareEditIcon,
  },
  {
    mode: 'desks', labelKey: 'floorplan-simple-mode-desks', hintKey: 'floorplan-simple-mode-desks-hint', Icon: DeskIcon,
  },
  {
    mode: 'labels', labelKey: 'floorplan-simple-mode-labels', hintKey: 'floorplan-simple-mode-labels-hint', Icon: FormatTextIcon,
  },
];

/** One labelled button. Labelled, not icon-only: nobody new to this should have to hover to find out. */
const ToolButton = ({
  label, hint, active, onClick, Icon, buttonRef, expanded,
}: {
  readonly label: string;
  readonly hint: string;
  readonly active: boolean;
  readonly onClick: () => void;
  readonly Icon: typeof CursorDefaultOutlineIcon;
  readonly buttonRef?: React.Ref<HTMLButtonElement>;
  readonly expanded?: boolean;
}): React.JSX.Element => (
  <Tooltip title={hint} placement="right">
    <ButtonBase
      ref={buttonRef}
      onClick={onClick}
      aria-pressed={active}
      aria-expanded={expanded}
      aria-label={label}
      sx={{
        width: 64,
        py: 0.75,
        flexDirection: 'column',
        gap: 0.25,
        borderRadius: `${editorTheme.radiusSmall}px`,
        color: active ? editorTheme.accentInk : editorTheme.inkDim,
        backgroundColor: active ? editorTheme.accent : 'transparent',
        transition: 'background-color 120ms, color 120ms',
        '&:hover': {
          backgroundColor: active ? editorTheme.accent : editorTheme.surfaceSunken,
          color: active ? editorTheme.accentInk : editorTheme.ink,
        },
        '&.Mui-focusVisible': { boxShadow: editorTheme.focusRing },
      }}
    >
      <Icon size={22} />
      <Typography component="span" sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1.1, color: 'inherit' }}>
        {label}
      </Typography>
    </ButtonBase>
  </Tooltip>
);

/**
 * The simple editor's toolbox: pick what to work on, then click it on the plan.
 *
 * Organised by the things on a floorplan rather than by drawing operations, and every button
 * carries its name. The shapes button opens a small tray of basic shapes — square, rectangle,
 * circle, triangle, line — each drawn onto the floor with two clicks.
 */
const SimpleToolbox = ({
  mode, tool, onModeChange, onShapeTool, showGrid, onShowGridChange, docked = false, drawTarget = 'floor',
}: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const shapesRef = useRef<HTMLButtonElement>(null);
  const [shapesOpen, setShapesOpen] = useState(false);

  // Lit while a basic shape is armed, so the admin can see which tool the next click belongs to.
  const shapeArmed = drawTarget === 'floor'
    && isCorneredTool(tool)
    && BASIC_SHAPES.some((shape) => shape.tool === tool);
  const ArmedIcon = BASIC_SHAPES.find((shape) => shape.tool === tool)?.Icon ?? ShapeOutlineIcon;

  return (
    <Box
      sx={{
        ...(docked ? {} : floatingSlabSx),
        position: 'static',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.25,
        p: 0.5,
      }}
      role="toolbar"
      aria-orientation="vertical"
      aria-label={String(t('floorplan-editor-tools'))}
    >
      {MODES.map(({
        mode: value, labelKey, hintKey, Icon,
      }) => (
        <ToolButton
          key={value}
          label={String(t(labelKey))}
          hint={String(t(hintKey))}
          // A shape being drawn belongs to the shapes button, not to whichever mode is behind it.
          active={mode === value && !shapeArmed}
          onClick={(): void => onModeChange(value)}
          Icon={Icon}
        />
      ))}

      <Divider flexItem sx={{ my: 0.25, borderColor: editorTheme.lineSoft }} />

      <ToolButton
        buttonRef={shapesRef}
        label={String(t('floorplan-simple-shapes'))}
        hint={String(t('floorplan-simple-shapes-hint'))}
        active={shapeArmed}
        expanded={shapesOpen}
        onClick={(): void => setShapesOpen(true)}
        Icon={ArmedIcon}
      />

      <Divider flexItem sx={{ my: 0.25, borderColor: editorTheme.lineSoft }} />

      <Tooltip title={`${String(t('floorplan-editor-grid-lines'))} (G)`} placement="right">
        <ButtonBase
          onClick={(): void => onShowGridChange(!showGrid)}
          aria-pressed={showGrid}
          aria-label={String(t('floorplan-editor-grid-lines'))}
          sx={{
            width: 64,
            py: 0.5,
            borderRadius: `${editorTheme.radiusSmall}px`,
            color: showGrid ? editorTheme.accent : editorTheme.inkFaint,
            backgroundColor: showGrid ? editorTheme.accentSoft : 'transparent',
            '&:hover': { backgroundColor: editorTheme.surfaceSunken },
            '&.Mui-focusVisible': { boxShadow: editorTheme.focusRing },
          }}
        >
          <GridIcon size={18} />
        </ButtonBase>
      </Tooltip>

      <Popover
        open={shapesOpen}
        anchorEl={shapesRef.current}
        onClose={(): void => setShapesOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { ml: 1, p: 1.25, borderRadius: `${editorTheme.radius}px`, width: 244 } }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 650, color: editorTheme.ink, mb: 0.25 }}>
          {t('floorplan-simple-shapes-title')}
        </Typography>
        <Typography sx={{ fontSize: 12, color: editorTheme.inkDim, mb: 1 }}>
          {t('floorplan-simple-shapes-help')}
        </Typography>
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.75 }}>
          {BASIC_SHAPES.map(({ tool: shapeTool, labelKey, Icon }) => (
            <ButtonBase
              key={shapeTool}
              onClick={(): void => {
                setShapesOpen(false);
                onShapeTool(shapeTool);
              }}
              aria-label={String(t(labelKey))}
              sx={{
                width: 70,
                py: 1,
                flexDirection: 'column',
                gap: 0.5,
                borderRadius: `${editorTheme.radiusSmall}px`,
                border: `1px solid ${shapeArmed && tool === shapeTool ? editorTheme.accent : editorTheme.line}`,
                color: shapeArmed && tool === shapeTool ? editorTheme.accent : editorTheme.inkDim,
                backgroundColor: shapeArmed && tool === shapeTool ? editorTheme.accentSoft : editorTheme.surface,
                '&:hover': { borderColor: editorTheme.accent, color: editorTheme.accent },
                '&.Mui-focusVisible': { boxShadow: editorTheme.focusRing },
              }}
            >
              <Icon size={26} />
              <Typography component="span" sx={{ fontSize: 11, fontWeight: 600, color: 'inherit' }}>
                {t(labelKey)}
              </Typography>
            </ButtonBase>
          ))}
        </Stack>
      </Popover>
    </Box>
  );
};

export default SimpleToolbox;
